import Vue from 'vue';
import axios from "axios";
import assign from 'lodash/assign';
import {injectGoalIfScoreChanged} from '@/services/goalDetector';
import {reapplyKeyOnMarkets} from '@/services/keyLiveReapply.service';

// v3.2 + Monotonic Shield (lastUpdate) for Sidebar Widget

// --- CONSTANTS ---
const EVENT_STATUS = {
    PREMATCH: 1,
    LIVE: 2,
    FINISHED: 3,
    STARTING_SOON: 9
};

// --- LOCAL LOOKUP TABLES ---
let eventsPrimaryLookup = {};
let oddsPrimaryLookup = {};

// --- GOAL BADGE TIMERS ---
const _goalTimers = new Map();

// --- TYPE CHANGE RESET TIMER ---
let _typeChangeResetTimer = null;
const TYPE_CHANGE_RESET_DELAY = 2500;

function _scheduleTypeChangeReset(commit) {
    if (_typeChangeResetTimer) clearTimeout(_typeChangeResetTimer);
    _typeChangeResetTimer = setTimeout(() => {
        commit('RESET_TYPE_CHANGES');
        _typeChangeResetTimer = null;
    }, TYPE_CHANGE_RESET_DELAY);
}

// --- HELPER: MONOTONIC UPDATE ---
// Estrae il timestamp in millisecondi in modo sicuro
function extractLastUpdate(payload) {
    if (!payload) return 0;
    if (payload.lastUpdate) return new Date(payload.lastUpdate).getTime();
    if (payload.livescore) {
        const ls = Array.isArray(payload.livescore) ? payload.livescore[0] : payload.livescore;
        if (ls && ls.lastUpdate) return new Date(ls.lastUpdate).getTime();
    }
    return 0;
}

// Funzione Scudo: ritorna true se il dato in arrivo è "vecchio"
function isPayloadStale(existing, incoming) {
    if (!existing || !incoming) return false;

    // 1. Check Status (non tornare mai indietro da Live a Prematch)
    const exStatus = parseInt(existing.status || existing.livescore?.scoreboard?.status || '0');
    let inStatus = parseInt(incoming.status || '0');
    if (inStatus === 0 && incoming.livescore) {
        const inLs = Array.isArray(incoming.livescore) ? incoming.livescore[0] : incoming.livescore;
        inStatus = parseInt(inLs?.scoreboard?.status || '0');
    }

    if ((inStatus === EVENT_STATUS.PREMATCH || inStatus === EVENT_STATUS.STARTING_SOON) &&
        (exStatus === EVENT_STATUS.LIVE || exStatus === EVENT_STATUS.FINISHED)) {
        return true;
    }

    // 2. Check Timestamp (Monotonicity)
    const exTime = extractLastUpdate(existing);
    const inTime = extractLastUpdate(incoming);
    if (exTime > 0 && inTime > 0 && inTime < exTime) return true;

    return false;
}

function mapPrimaryOddsToLookup(market, eventId) {
    if (!market || !market.outcome || !Array.isArray(market.outcome)) return;
    for (let k = 0; k < market.outcome.length; k++) {
        const odd = market.outcome[k];
        if (odd.unique_id) {
            // Salviamo anche l'eventId per sicurezza cross-aggiornamento
            oddsPrimaryLookup[odd.unique_id] = {odd: odd, market: market, eventId: eventId || null};
        }
    }
}

const state = {
    liveEventsPrimaryMarket: [],
    liveEventsPrimaryMarketSports: [],
    sportSelected: "",
    loading: false
};

const getters = {
    getFetchPrimaryMarketLoading: (state) => state.loading,
    getEventPrimaryMarket: (state) => state.liveEventsPrimaryMarket,
    getEventPrimaryMarketSports: (state) => state.liveEventsPrimaryMarketSports,
    getSportSelected: (state) => state.sportSelected
};

const mutations = {
    SET_LIVE_EVENT_PRIMARY_MARKET(state, payload) {
        state.liveEventsPrimaryMarket = payload;

        // Reset indici
        eventsPrimaryLookup = {};
        oddsPrimaryLookup = {};
        _goalTimers.forEach(timerId => clearTimeout(timerId));
        _goalTimers.clear();
        if (_typeChangeResetTimer) { clearTimeout(_typeChangeResetTimer); _typeChangeResetTimer = null; }

        if (payload && Array.isArray(payload)) {
            payload.forEach(group => {
                if (Array.isArray(group)) {
                    group.forEach(event => {
                        if (event.id) {
                            eventsPrimaryLookup[event.id] = event;
                            if (event.odds && Array.isArray(event.odds)) {
                                event.odds.forEach(m => mapPrimaryOddsToLookup(m, event.id));
                            }
                        }
                    });
                }
            });
        }
    },
    SET_LIVE_EVENT_PRIMARY_MARKET_SPORTS_AVAILABLE(state, payload) {
        state.liveEventsPrimaryMarketSports = payload;
    },
    SET_SPORT_SELECTED(state, payload) {
        state.sportSelected = payload;
    },
    SET_LOADING(state, payload) {
        state.loading = payload;
    },

    // --- SOCKET UPDATES: LIVESCORE ---
    UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
        const updates = Array.isArray(payload) ? payload : [payload];
        for (let i = 0; i < updates.length; i++) {
            const change = updates[i];
            const event = eventsPrimaryLookup[change.id];

            if (!event) continue;

            if (isPayloadStale(event, change)) continue;

            // Aggiorna lastUpdate dell'evento
            if (change.lastUpdate) Vue.set(event, 'lastUpdate', change.lastUpdate);
            if (change.status != null) event.status = String(change.status);

            if (change.livescore) {
                const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
                if (!incomingData) continue;

                // Goal detection
                const goalPosition = injectGoalIfScoreChanged(event, {...change, livescore: incomingData});
                if (goalPosition !== null) {
                    if (_goalTimers.has(change.id)) clearTimeout(_goalTimers.get(change.id));
                    const eventId = change.id;
                    _goalTimers.set(eventId, setTimeout(() => {
                        const evt = eventsPrimaryLookup[eventId];
                        if (evt?.livescore?.livescoreExtraData) {
                            evt.livescore.livescoreExtraData = evt.livescore.livescoreExtraData.filter(e => e.type !== 9);
                        }
                        _goalTimers.delete(eventId);
                    }, 10000));
                }

                if (!event.livescore) {
                    Vue.set(event, 'livescore', incomingData);
                } else {
                    if (incomingData.scoreboard) {
                        if (!event.livescore.scoreboard) event.livescore.scoreboard = {};
                        assign(event.livescore.scoreboard, incomingData.scoreboard);
                    }
                    if (incomingData.lastUpdate) event.livescore.lastUpdate = incomingData.lastUpdate;
                    if (incomingData.livescoreExtraData) event.livescore.livescoreExtraData = incomingData.livescoreExtraData;
                    if (incomingData.periods) event.livescore.periods = incomingData.periods;
                    if (incomingData.statistics) event.livescore.statistics = incomingData.statistics;
                    if (incomingData.matchStatus) event.livescore.matchStatus = incomingData.matchStatus;
                }
            }
        }
    },

    // --- SOCKET UPDATES: ODDS ---
    UPDATE_ODDS_FROM_SOCKET(state, payload) {
        if (!payload || !Array.isArray(payload)) return;

        // Same dirty-set pattern as detailLive / previewLive: one KEY reapply
        // per market at the end, dedup'd via Set.
        const dirtyMarkets = new Set();

        for (let i = 0; i < payload.length; i++) {
            const eventPayload = payload[i];
            if (eventPayload.odds && eventPayload.odds.set) {
                const oddChanges = eventPayload.odds.set;

                for (let j = 0; j < oddChanges.length; j++) {
                    const changeItem = oddChanges[j];
                    const uniqueId = changeItem.unique_id;
                    const attr = changeItem._attributes;
                    const lookupRef = oddsPrimaryLookup[uniqueId];

                    if (lookupRef && attr) {
                        const targetOdd = lookupRef.odd;

                        const inTime = attr.lastUpdate ? new Date(attr.lastUpdate).getTime() : 0;
                        if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) {
                            continue; // Ignora quota vecchia
                        }

                        const targetMarket = lookupRef.market;
                        const newValue = parseFloat(attr.odds);
                        const oldValue = parseFloat(targetOdd.outcome_odd_value);

                        if (newValue > oldValue) Vue.set(targetOdd, 'type_change', 'up');
                        else if (newValue < oldValue) Vue.set(targetOdd, 'type_change', 'down');

                        // Socket carries the RAW provider value → track both
                        // baseline (outcome_odd_original) and display
                        // (outcome_odd_value). KEY reapply overwrites display
                        // when enabled.
                        targetOdd.outcome_odd_original = newValue;
                        targetOdd.outcome_odd_value = newValue;
                        targetOdd.status = parseInt(attr.status);
                        // Freeze-suspend authoritative — task 13.
                        if (targetOdd.frozenApplied !== true) {
                            targetOdd.active = (targetOdd.status === 1);
                        }
                        // Boost invalidation on socket tick — see detailLive
                        // for rationale (raw provider odd wins, badge would
                        // otherwise stick when the boost rule no longer
                        // applies to the current context).
                        if (targetOdd.boostApplied === true) {
                            Vue.set(targetOdd, 'boostApplied', false);
                            Vue.set(targetOdd, 'boostPercent', null);
                            Vue.set(targetOdd, 'oddsPreBoost', null);
                        }
                        if (inTime > 0) targetOdd.lastUpdate = inTime;

                        if (targetMarket && attr.line) targetMarket.specifier = attr.line;
                        // R.1 early bailout — skip markets without KEY rule.
                        if (targetMarket && targetMarket.keyApplied != null) dirtyMarkets.add(targetMarket);
                    }
                }
            }
        }

        if (dirtyMarkets.size > 0) reapplyKeyOnMarkets(dirtyMarkets);
    },

    RESET_TYPE_CHANGES() {
        for (const key in oddsPrimaryLookup) {
            const ref = oddsPrimaryLookup[key];
            if (ref?.odd?.type_change != null) Vue.set(ref.odd, 'type_change', null);
        }
    }
};

const actions = {
    async fetchLivePrimaryMarket(context, payload) {
        context.commit('SET_LOADING', true);
        const request = {
            sportId: (payload && payload.sport) ? payload.sport : undefined,
            platformId: context.rootGetters['settings/getPlatformId'],
            page: 1,
            limit: 10
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        try {
            const res = await axios.post(`${apiUrl}/v3/events/live/primary-market`, request, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            const result = res.data?.data?.result;
            if (result) {
                if (result.sportsAvailable) context.commit('SET_LIVE_EVENT_PRIMARY_MARKET_SPORTS_AVAILABLE', result.sportsAvailable);
                if (result.groupedEvents) context.commit('SET_LIVE_EVENT_PRIMARY_MARKET', result.groupedEvents);
                else context.commit('SET_LIVE_EVENT_PRIMARY_MARKET', []);
                context.commit('SET_SPORT_SELECTED', payload.sport);
            } else {
                context.commit('SET_LIVE_EVENT_PRIMARY_MARKET', []);
            }
        } catch (error) {
            console.error("Error fetchLivePrimaryMarket:", error);
            context.commit('SET_LIVE_EVENT_PRIMARY_MARKET', []);
        } finally {
            context.commit('SET_LOADING', false);
        }
    },

    setSportSelected(context, payload) {
        context.commit('SET_SPORT_SELECTED', payload);
    },

    socketUpdateOdds({commit}, payload) {
        commit('UPDATE_ODDS_FROM_SOCKET', payload);
        _scheduleTypeChangeReset(commit);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};